import { observable } from 'mobx';
import moment from 'moment';
import type { Moment } from '../lib/moment';
import type OrderItem from './order_item2';
import type {
  HotelElementGuestArgs,
  HotelElementGuestResponseArgs
} from './hotel_element_guest/hotel_element_guest2';
import HotelElementGuest, {
  convertHotelElementGuestResponseToArgs
} from './hotel_element_guest/hotel_element_guest2';

export type HotelElementResponseArgs = {
  id: number;
  name: string;
  room_num: number;
  checkin_date: string;
  checkout_date: string;
  provider: string;
  plan_name: string;
  room_type: string;
  breakfast?: boolean;
  restricted: boolean;
  too_late: boolean;
  hotel_element_guests: HotelElementGuestResponseArgs[];
  package_type?: string;
  marketlog_available?: boolean;
};

export type HotelElementArgs = {
  id: number;
  name: string;
  roomNum: number;
  checkinDate: Moment;
  checkoutDate: Moment;
  provider: string;
  planName: string;
  roomType: string;
  breakfast?: boolean;
  restricted: boolean;
  tooLate: boolean;
  hotelElementGuests: HotelElementGuestArgs[];
  packageType?: string;
  marketlogAvailable?: boolean;
  detailPath?: string;
};

export const convertHotelElementResponseToArgs = (response: HotelElementResponseArgs) => {
  const args: HotelElementArgs = {
    id: response.id,
    name: response.name,
    roomNum: response.room_num,
    checkinDate: moment(response.checkin_date),
    checkoutDate: moment(response.checkout_date),
    provider: response.provider,
    planName: response.plan_name,
    roomType: response.room_type,
    breakfast: response.breakfast,
    restricted: response.restricted,
    tooLate: response.too_late,
    hotelElementGuests: response.hotel_element_guests.map(convertHotelElementGuestResponseToArgs),
    packageType: response.package_type,
    marketlogAvailable: response.marketlog_available
  };
  return args;
};

export default class HotelElement {
  @observable
  id: number;

  @observable
  name: string;

  @observable
  roomNum: number;

  @observable
  checkinDate: Moment;

  @observable
  checkoutDate: Moment;

  @observable
  provider: string;

  @observable
  planName: string;

  @observable
  roomType: string;

  @observable
  breakfast?: boolean;

  @observable
  restricted: boolean;

  @observable
  tooLate: boolean;

  @observable
  hotelElementGuests: HotelElementGuest[] = [];

  @observable
  packageType?: string;

  @observable
  marketlogAvailable?: boolean;

  @observable
  detailPath?: string;

  constructor(args: HotelElementArgs) {
    this.id = args.id;
    this.name = args.name;
    this.roomNum = args.roomNum;
    this.checkinDate = args.checkinDate;
    this.checkoutDate = args.checkoutDate;
    this.provider = args.provider;
    this.planName = args.planName;
    this.roomType = args.roomType;
    this.breakfast = args.breakfast;
    this.restricted = args.restricted;
    this.tooLate = args.tooLate;
    this.hotelElementGuests = args.hotelElementGuests.map(guest => new HotelElementGuest(guest));
    this.packageType = args.packageType;
    this.marketlogAvailable = args.marketlogAvailable;
  }

  get type() {
    return 'hotel';
  }

  get summary() {
    return `${this.checkinDate.format('YYYY-MM-DD')} 〜 ${this.checkoutDate.format('YYYY-MM-DD')}`;
  }

  get title() {
    return '宿泊';
  }

  get breakfastDescription() {
    let d = '\n朝食：';
    switch (this.breakfast) {
      case true:
        d += '有り';
        break;
      case false:
        d += '無し';
        break;
      default:
        d += 'お問合せください';
        break;
    }
    return d;
  }

  get marketLogType() {
    return this.type;
  }

  get isPackage() {
    return !!this.packageType || this.provider === 'wbf';
  }

  detail(orderItem: OrderItem, opts: { inlineRestricted: boolean } = { inlineRestricted: false }) {
    return this.structuredDetail(orderItem, opts)
      .map(line => `${line.label}${line.value}\n`)
      .join('');
  }

  structuredDetail(_orderItem: OrderItem, opts: { inlineRestricted: boolean } = { inlineRestricted: false }) {
    const lines = this.structuredHotelTypeDetail(opts);
    lines.push(...this.structuredRoomTypeDetail());

    return lines;
  }

  structuredHotelTypeDetail(opts: { inlineRestricted: boolean } = { inlineRestricted: false }) {
    const lines = [];
    lines.push({
      label: opts.inlineRestricted && this.restricted ? 'RESTRICTED!宿泊プラン：' : '宿泊プラン：',
      value: this.planName ? this.planName : '-',
      translate: true,
      restricted: this.restricted
    });

    if (this.provider !== 'agoda') {
      lines.push({
        label: '部屋タイプ：',
        value: this.roomType ? this.roomType : '',
        translate: true
      });
    }
    lines.push({
      label: '',
      value: this.breakfastDescription,
      translate: true
    });
    return lines;
  }

  structuredRoomTypeDetail() {
    const lines: Array<{ label: string; value: string; translate: boolean }> = [];
    this.hotelElementGuests.forEach((guest, i) => {
      lines.push(
        {
          label: '',
          value: `<${i + 1}室目>`,
          translate: false
        },
        {
          label: '',
          value: guest.bedTypeDescriptionOverview,
          translate: false
        },
        {
          label: '',
          value: `${guest.guestLastName} ${guest.guestFirstName} (${guest.guestTel})`,
          translate: false
        }
      );
    });
    return lines;
  }
}
